import { Box, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useState, useRef } from 'react';
import { pca as PublicClientApplication } from '@/Contexts/AuthWrapper';

import ADFSignUp from '@/components/signup_components/ADFSignUp';
import PartnerSignUpForm from '@/components/signup_components/PartnerSignup/PartnerSignUpForm';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeProvider } from '@/Contexts/PartnerIntakeContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { UserProps, UserType } from '@/models/User';

import Header from '../components/header/Header';
import { TokenContext } from '@/Contexts/TokenContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { useNavigate } from 'react-router-dom';
import useUser from '@/hooks/useUser';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { CacheLookupPolicy } from '@azure/msal-browser';
import { IntakeResponse } from '@/models/IntakeResponse';
import { ConfigContext } from '@/Contexts/ConfigContext';
import IntakeProgramSelect from '@/components/signup_components/PartnerSignup/common/IntakeProgramSelect';
import StickyHeader from '@/components/common/StickyHeader';
import { Spinner } from '@/components/common/Spinner';
import { ProgramNames } from '@/models/Program';

function NewUserIntake() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { getUser, postUser } = useUser();
  const { translatedText } = useContext(LocaleContext);
  const { currentContact } = useContext(UsersContext);
  const { partnerId, partnerGroupId, updatePartnerGroupId, updatePartnerId } =
    useContext(SelectedPartnerContext);
  const { validated, b2cId, updateValidated } = useContext(TokenContext);
  const { userType, userCurrent, updateContactId, updateUserCurrent } = useContext(UsersContext);
  const { setAlert } = useContext(SnackbarContext);
  const { allPrograms } = useContext(ConfigContext);

  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [selectedProgramId, setSelectedProgramId] = useState<ProgramNames | null>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (scrollRef.current != null) {
      setShowStickyHeader(scrollRef.current.scrollTop > 50);
    }
  };

  useEffect(() => {
    if (
      currentContact != null &&
      partnerGroupId != null &&
      partnerId != null &&
      validated &&
      !isNavigating
    ) {
      setIsNavigating(true);
      navigate('/dashboard');
    }
  }, [currentContact, partnerGroupId, partnerId, validated, isNavigating, navigate]);

  const handleSubmit = async (intakeResponse: IntakeResponse) => {
    if (userCurrent?.userImpersonated != null) {
      const impersonateInfo: UserProps = {
        ...userCurrent,
        b2CId: userCurrent.userImpersonated,
        partnerGroupId: intakeResponse.partnerGroupId,
        contactId: intakeResponse.contactId,
      };
      const userData = await postUser(impersonateInfo);
      updateUserCurrent(userData);
    }

    if (b2cId != null) {
      const res = await getUser(b2cId);
      if (res == null) {
        setAlert({
          severity: 'error',
          message: translatedText.signup.messageSubmissionError,
        });
        return;
      }

      updateUserCurrent(res);
      setAlert({
        severity: 'success',
        message: translatedText.signup.messageSubmissionSuccess,
      });
      updateValidated(res.validated);
      updateContactId(intakeResponse.contactId);
      updatePartnerId(intakeResponse.partnerId);
      updatePartnerGroupId(intakeResponse.partnerGroupId);
      await PublicClientApplication.acquireTokenSilent({
        scopes: [import.meta.env.VITE_SCOPE as string],
        cacheLookupPolicy: CacheLookupPolicy.Skip,
      });

      navigate('/');
    }
  };

  const handleProgramChange = (programId: ProgramNames) => {
    setSelectedProgramId(programId);
  };

  return (
    translatedText != null && (
      <Box
        display='flex'
        flexDirection='column'
        height='100vh'
        width='100vw'
        sx={{
          overflowX: 'hidden',
        }}
      >
        <Header />

        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <StickyHeader
            title={translatedText?.signup?.headerProgram ?? ''}
            showStickyHeader={showStickyHeader}
          />
          <Box display='flex' flexDirection='column'>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              justifyContent='center'
              sx={{
                height: '100px',
                color: theme.palette.taiga.main,
                paddingTop: '10px',
              }}
            >
              <Box />
              <Typography variant='h1'>{translatedText?.signup?.headerProgram ?? ''}</Typography>
            </Box>
          </Box>
          <Box display='flex' flex={1} justifyContent='center'>
            <Box
              display='flex'
              flexDirection='column'
              flexGrow={1}
              gap='20px'
              minWidth='300px'
              maxWidth='1000px'
              padding={5}
              marginBottom={3}
              borderColor={theme.palette.sandShade2.main}
              alignItems='center'
              sx={{
                backgroundColor: theme.palette.sandShade1.main,
                paddingBottom: '60px',
              }}
            >
              {userType === UserType.Applicant && (
                <>
                  {(allPrograms?.length ?? 0) > 0 ? (
                    <IntakeProgramSelect onChange={handleProgramChange} />
                  ) : (
                    <Spinner type='circular' />
                  )}
                  <PartnerIntakeProvider programId={selectedProgramId}>
                    <PartnerSignUpForm onSubmit={handleSubmit} />
                  </PartnerIntakeProvider>
                </>
              )}
              {(userType === UserType.Adf ||
                userType === UserType.StateCoordinator ||
                userType === UserType.RegionalCoordinator ||
                userType === UserType.NationalProgrammeLead) && <ADFSignUp />}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  );
}

export default NewUserIntake;
